import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import siteConfig from "../../config/site";

const API_URL = siteConfig.apiUrl;

export const NavBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [user, setUser] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Check auth status
  useEffect(() => {
    fetch(`${API_URL}/auth/checkauth`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.authenticated) {
          setUser(data);
        }
      })
      .catch(() => {});
  }, [location.pathname]);

  // Close menu on route change
  useEffect(() => {
    setIsOpen(false);
    setShowDropdown(false);
  }, [location.pathname]);

  // Track scroll for navbar background
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return;
    const handleClick = () => setShowDropdown(false);
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [showDropdown]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const handleLogout = async () => {
    await fetch(`${API_URL}/auth/logout`, { method: "POST", credentials: "include" });
    setUser(null);
    setShowDropdown(false);
    navigate("/");
  };

  const getDashboardLink = () => {
    if (!user) return "/login";
    if (user.role === "admin") return "/admin";
    if (user.role === "performer") return "/profile";
    return "/profile";
  };

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/entertainers", label: "Entertainers" },
    { to: "/services", label: "Services" },
    { to: "/about", label: "About" },
    { to: "/inspirations", label: "Inspirations" },
    { to: "/book", label: "Book Now", cta: true },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || isOpen
            ? "bg-black/90 backdrop-blur-md shadow-lg shadow-black/30"
            : "bg-gradient-to-b from-black/60 to-transparent"
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Brand */}
            <Link to="/" className="flex items-center gap-2 group">
              <img
                src="/images/exo-logo-nav.png"
                alt="Exo Entertainment"
                className="h-8 sm:h-10 w-auto opacity-90 group-hover:opacity-100 transition-opacity duration-300"
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) =>
                link.cta ? (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="ml-3 px-6 py-2 bg-gold text-black font-bold text-xs uppercase tracking-widest rounded-full hover:bg-gold-light transition-all duration-300 shadow-md shadow-gold/20"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`px-4 py-2 text-xs uppercase tracking-wider font-medium rounded-lg transition-all duration-300 ${
                      location.pathname === link.to
                        ? "text-gold"
                        : "text-gray-300 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              )}

              {/* Auth: Login icon OR user dropdown */}
              {user ? (
                <div className="relative ml-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowDropdown(!showDropdown); }}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-white/5 transition-all duration-300"
                  >
                    <div className="w-6 h-6 rounded-full bg-purple-700/60 flex items-center justify-center text-white text-xs font-bold">
                      {user.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <svg className={`w-3 h-3 transition-transform ${showDropdown ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {showDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-gray-900 border border-gray-700/50 rounded-xl shadow-xl overflow-hidden">
                      <div className="px-4 py-3 border-b border-gray-800">
                        <p className="text-white text-sm font-medium truncate">{user.name}</p>
                        <p className="text-gray-500 text-xs capitalize">{user.role}</p>
                      </div>
                      <Link
                        to={getDashboardLink()}
                        className="block px-4 py-2.5 text-gray-300 hover:text-white hover:bg-white/5 text-sm transition-colors"
                      >
                        {user.role === "admin" ? "Dashboard" : "My Profile"}
                      </Link>
                      {user.role === "admin" && (
                        <Link
                          to="/profile"
                          className="block px-4 py-2.5 text-gray-300 hover:text-white hover:bg-white/5 text-sm transition-colors"
                        >
                          My Profile
                        </Link>
                      )}
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2.5 text-gray-400 hover:text-red-400 hover:bg-white/5 text-sm transition-colors border-t border-gray-800"
                      >
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/login"
                  className={`ml-2 px-3 py-2 text-xs uppercase tracking-wider font-medium rounded-lg transition-all duration-300 ${
                    location.pathname === "/login"
                      ? "text-gold"
                      : "text-gray-500 hover:text-gray-300 hover:bg-white/5"
                  }`}
                  title="Performer & Admin Login"
                >
                  <svg className="w-4 h-4 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                  </svg>
                </Link>
              )}
            </div>

            {/* Mobile Hamburger */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden relative w-10 h-10 flex items-center justify-center text-white focus:outline-none"
              aria-label="Toggle menu"
            >
              <div className="flex flex-col gap-1.5">
                <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${isOpen ? "rotate-45 translate-y-2" : ""}`} />
                <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${isOpen ? "opacity-0" : ""}`} />
                <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${isOpen ? "-rotate-45 -translate-y-2" : ""}`} />
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-[#0a0a0f]/98 backdrop-blur-md transition-all duration-500 md:hidden ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="flex flex-col items-center justify-center h-full gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setIsOpen(false)}
              className={`text-3xl font-bold transition-all duration-300 font-heading ${
                link.cta
                  ? "px-10 py-3 bg-gold text-black rounded-full hover:bg-gold-light mt-4"
                  : location.pathname === link.to
                  ? "text-gold"
                  : "text-white hover:text-gold"
              }`}
            >
              {link.label}
            </Link>
          ))}

          {/* Auth section in mobile menu */}
          {user ? (
            <div className="mt-4 text-center space-y-3">
              <Link
                to={getDashboardLink()}
                onClick={() => setIsOpen(false)}
                className="block text-sm text-purple-400 hover:text-purple-300 transition-all duration-300 uppercase tracking-wider"
              >
                {user.role === "admin" ? "Dashboard" : "My Profile"}
              </Link>
              <button
                onClick={() => { handleLogout(); setIsOpen(false); }}
                className="text-sm text-gray-500 hover:text-red-400 transition-all duration-300 uppercase tracking-wider"
              >
                Sign Out
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              onClick={() => setIsOpen(false)}
              className="text-sm text-gray-500 hover:text-gray-300 transition-all duration-300 mt-2 uppercase tracking-wider"
            >
              Performer / Admin Login
            </Link>
          )}

          <div className="mt-8 text-center">
            <p className="text-gray-500 text-sm">{siteConfig.contact.email}</p>
            <p className="text-gray-600 text-xs mt-1">Calgary, AB · 18+</p>
          </div>
        </div>
      </div>
    </>
  );
};
